import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import api from '../../api';
import {
    Users, Shield, Activity, MessageSquare,
    Plus, Search, Trash2, Edit3, Globe,
    Code, Image as ImageIcon, Check, X,
    Loader2, ChevronRight, BarChart3, AlertCircle,
    BrainCircuit, Sparkles, Filter, Zap, ShieldCheck, Settings, Key, Cpu, RefreshCw, Calendar,
    LayoutDashboard, History, HeartPulse, Terminal, Bell, ExternalLink, ChevronLeft, Upload, UserPlus,
    ThumbsUp, ThumbsDown, ChevronDown, Menu, ChevronsRight
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BulkImportModal } from '../ui/BulkImportModal';
import { ScholarsAnchorProvider } from '../../benchrex/context/BenchrexContext.tsx';
const ChatPage = React.lazy(() => import('../../benchrex/pages/ChatPage.tsx'));
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '../../benchrex/components/ui/tooltip.tsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import useCalendarStore from '../../store/useCalendarStore';
import { getProfile, getAccessLevel } from '../../lib/rbac';

export default function BenchrexPanel() {
    const queryClient = useMemo(() => new QueryClient(), []);
    const profile = useMemo(() => getProfile(), []);
    const level = useMemo(() => getAccessLevel(), []);

    const darkMode = useCalendarStore(s => s.darkMode);
    const setGlobalDarkMode = useCalendarStore(s => s.setDarkMode);

    const activeTab = useCalendarStore(s => s.scholarsanchorActiveTab);
    const setActiveTab = useCalendarStore(s => s.setScholarsAnchorActiveTab);
    const sidebarCollapsed = useCalendarStore(s => s.scholarsanchorSidebarCollapsed);
    const setSidebarCollapsed = useCalendarStore(s => s.setScholarsAnchorSidebarCollapsed);
    const targetConversationId = useCalendarStore(s => s.scholarsanchorTargetConversationId);
    const setTargetConversationId = useCalendarStore(s => s.setScholarsAnchorTargetConversationId);
    const selectedUserId = useCalendarStore(s => s.scholarsanchorSelectedUserId);
    const setSelectedUserId = useCalendarStore(s => s.setScholarsAnchorSelectedUserId);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const setMobileSidebarOpen = useCalendarStore(s => s.setMobileSidebarOpen);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [consoleSidebarOpen, setConsoleSidebarOpen] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        users: 0,
        personalities: 0,
        logs: 0,
        feedback: 0,
        queries: 0,
        total_cost: 0
    });

    const [personalities, setPersonalities] = useState([]);
    const [logs, setLogs] = useState([]);
    const [logsPage, setLogsPage] = useState(1);
    const [hasMoreLogs, setHasMoreLogs] = useState(true);
    const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
    const [feedback, setFeedback] = useState([]);
    const [queries, setQueries] = useState([]);
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [userConversations, setUserConversations] = useState([]);
    const [loadingConvs, setLoadingConvs] = useState(false);
    const [sidebarMode, setSidebarMode] = useState('threads'); // 'threads' | 'users'
    const [chartHorizon, setChartHorizon] = useState('24h');
    const [chartLogs, setChartLogs] = useState([]);
    const [models, setModels] = useState([]);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingPersonality, setEditingPersonality] = useState(null);
    const [newPersonality, setNewPersonality] = useState({
        name: '',
        model: '',
        model_id: '',
        system_instructions: '',
        description: '',
        icon: '🤖',
        tool_web_search: false,
        tool_code_interpreter: false,
        tool_image_gen: false,
        tool_calendar_mgmt: false,
        tool_chain_of_thought: false,
        api_key: '',
    });

    const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
    const [bulkImportOpen, setBulkImportOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        role: 'student',
        credits: 100,
        grade: '',
        course: '',
        batch: '',
        password: ''
    });

    const [isConfigOpen, setIsConfigOpen] = useState(false);


    // Dynamic chart data calculation from chartLogs
    const chartData = useMemo(() => {
        if (!chartLogs || chartLogs.length === 0) {
            return [
                { name: '00:00', volume: 0 },
                { name: '04:00', volume: 0 },
                { name: '08:00', volume: 0 },
                { name: '12:00', volume: 0 },
                { name: '16:00', volume: 0 },
                { name: '20:00', volume: 0 },
                { name: '23:59', volume: 0 },
            ];
        }

        if (chartHorizon === '24h') {
            // Group logs by hour
            const hourlyData = chartLogs.reduce((acc, log) => {
                const date = new Date(log.created_at);
                const hour = date.getHours();
                acc[hour] = (acc[hour] || 0) + 1;
                return acc;
            }, {});

            // Format for Recharts (showing 24 hours)
            return Array.from({ length: 24 }, (_, i) => ({
                name: `${i.toString().padStart(2, '0')}:00`,
                volume: hourlyData[i] || 0
            }));
        } else {
            // Group logs by day for 7d and 30d
            const dailyData = chartLogs.reduce((acc, log) => {
                const date = new Date(log.created_at);
                const day = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                acc[day] = (acc[day] || 0) + 1;
                return acc;
            }, {});

            // Generate all days in the range to fill gaps
            const daysCount = chartHorizon === '7d' ? 7 : 30;
            const result = [];
            const now = new Date();

            for (let i = daysCount - 1; i >= 0; i--) {
                const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
                const dayKey = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                result.push({
                    name: dayKey,
                    volume: dailyData[dayKey] || 0
                });
            }

            return result;
        }
    }, [chartLogs, chartHorizon]);

    const userConversationCounts = useMemo(() => {
        const counts = {};
        queries.forEach(q => {
            if (q.user_id) {
                counts[q.user_id] = (counts[q.user_id] || 0) + 1;
            }
        });
        return counts;
    }, [queries]);

    // Computed alerts based on logs and system state
    const computedAlerts = useMemo(() => {
        const alerts = [];
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
        const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        // 1. High Traffic Check
        const recentLogs = logs.filter(l => new Date(l.created_at) > oneHourAgo);
        if (recentLogs.length > 20) {
            alerts.push({
                title: 'High Traffic Peak',
                time: 'Last Hour',
                type: 'info',
                icon: Zap,
                description: `${recentLogs.length} operations detected.`
            });
        }

        // 2. System Errors Check
        const errorLogs = recentLogs.filter(l => l.status !== 'success');
        if (errorLogs.length > 0) {
            alerts.push({
                title: 'System Errors Detected',
                time: 'Recent',
                type: 'urgent',
                icon: AlertCircle,
                description: `${errorLogs.length} failed requests in 60m.`
            });
        }

        // 3. Pending Expert Sessions
        const unreadRequests = queries.filter(q => q.is_expert_session && q.expert_has_unread);
        if (unreadRequests.length > 0) {
            alerts.push({
                title: 'Unread Expert Requests',
                time: 'Live',
                type: 'urgent',
                icon: Users,
                description: `${unreadRequests.length} students waiting.`
            });
        }

        // 4. New Signups
        const newUsersCount = users.filter(u => new Date(u.created_at) > last24Hours).length;
        if (newUsersCount > 0) {
            alerts.push({
                title: 'New Student Onboarding',
                time: 'Today',
                type: 'success',
                icon: UserPlus,
                description: `${newUsersCount} students joined today.`
            });
        }

        // Fallback if no alerts
        if (alerts.length === 0) {
            alerts.push({
                title: 'System Active',
                time: 'Now',
                type: 'info',
                icon: ShieldCheck,
                description: 'Monitoring active across all nodes.'
            });
        }

        return alerts.slice(0, 5);
    }, [logs, queries, users]);

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    useEffect(() => {
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const isSuperAdmin = profile.access_level === 1 && !profile.team_id;

        const channel = supabase
            .channel('benchrex-conversations-realtime')
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'conversations' },
                async (payload) => {
                    if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
                        const updated = payload.new;
                        
                        // If it's a team console, verify it belongs to this team
                        if (!isSuperAdmin && profile.team_id && updated.team_id !== profile.team_id) {
                            return;
                        }

                        if (payload.eventType === 'INSERT') {
                            const { data: userData } = await supabase
                                .from('benchrex_users')
                                .select('name, email')
                                .eq('id', updated.user_id)
                                .maybeSingle();
                            
                            updated.user = userData || null;
                            setQueries(prev => {
                                if (prev.some(q => q.id === updated.id)) return prev;
                                return [updated, ...prev];
                            });
                        } else {
                            // UPDATE
                            setQueries(prev => {
                                return prev.map(q => q.id === updated.id ? { ...q, ...updated } : q);
                            });
                        }
                    } else if (payload.eventType === 'DELETE') {
                        setQueries(prev => prev.filter(q => q.id !== payload.old.id));
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    useEffect(() => {
        setStats(prev => ({
            ...prev,
            queries: queries.filter(q => q.is_expert_session && q.expert_has_unread).length
        }));
    }, [queries]);

    useEffect(() => {
        fetchChartData();
        fetchModels();
    }, [chartHorizon]);


    const [batches, setBatches] = useState([]);
    const [courses, setCourses] = useState([]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const isSuperAdmin = profile.access_level === 1 && !profile.team_id;

            // Reset logs page and fetch initial logs
            setLogsPage(1);
            await fetchLogs(1, true);
            await fetchChartData();

            if (profile.team_id) {
                const { data: tRes } = await supabase.from('teams').select('name').eq('id', profile.team_id).maybeSingle();
                if (tRes) setTeamName(tRes.name);
            } else if (isSuperAdmin) {
                setTeamName('Global Administration');
            }

            // Construct queries with conditional team filter
            const buildQuery = (table) => {
                let q = supabase.from(table).select('*');
                if (!isSuperAdmin && profile.team_id) {
                    q = q.eq('team_id', profile.team_id);
                }
                return q;
            };

            const [fRes, uRes, cRes, pRes, bRes, crsRes] = await Promise.all([
                (() => {
                    let q = supabase.from('benchrex_feedback').select('*, user:benchrex_users(name, email)');
                    if (!isSuperAdmin && profile.team_id) q = q.eq('team_id', profile.team_id);
                    return q.order('created_at', { ascending: false }).limit(100);
                })(),
                buildQuery('benchrex_users').order('created_at', { ascending: false }),
                (() => {
                    let q = supabase.from('conversations').select('*, user:benchrex_users(name, email)');
                    if (!isSuperAdmin && profile.team_id) q = q.eq('team_id', profile.team_id);
                    return q.order('updated_at', { ascending: false });
                })(),
                buildQuery('personalities'),
                buildQuery('batches').order('name'),
                buildQuery('courses')
            ]);

            if (fRes.data) setFeedback(fRes.data);
            if (uRes.data) setUsers(uRes.data);
            if (cRes.data) setQueries(cRes.data);
            if (pRes.data) setPersonalities(pRes.data);
            if (bRes.data) setBatches(bRes.data);
            if (crsRes.data) setCourses(crsRes.data);

            // Stats with same conditional logic
            const buildCountQuery = (table) => {
                let q = supabase.from(table).select('*', { count: 'exact', head: true });
                if (!isSuperAdmin && profile.team_id) q = q.eq('team_id', profile.team_id);
                return q;
            };

            const [uCount, pCount, fCount, lCount, costRes] = await Promise.all([
                buildCountQuery('benchrex_users'),
                buildCountQuery('personalities'),
                buildCountQuery('benchrex_feedback'),
                buildCountQuery('benchrex_logging'),
                supabase.rpc('get_team_total_cost', { p_team_id: isSuperAdmin ? null : profile.team_id })
            ]);

            setStats({
                users: uCount.count || 0,
                personalities: pCount.count || 0,
                logs: lCount.count || 0,
                feedback: fCount.count || 0,
                queries: cRes.data?.filter(q => q.is_expert_session && q.expert_has_unread).length || 0,
                total_cost: costRes.data || 0
            });
        } catch (err) {
            console.error("Fetch error:", err);
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const fetchModels = async () => {
        try {
            const res = await api.get('/api/benchrex/models');
            if (res.data) setModels(res.data);
        } catch (err) {
            console.error("Failed to fetch models:", err);
        }
    };

    const fetchChartData = async () => {
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const isSuperAdmin = profile.access_level === 1 && !profile.team_id;
            const now = new Date();
            let startDate;

            if (chartHorizon === '24h') {
                startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            } else if (chartHorizon === '7d') {
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            } else {
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            }

            let q = supabase
                .from('benchrex_logging')
                .select('created_at')
                .gte('created_at', startDate.toISOString());

            if (!isSuperAdmin && profile.team_id) {
                q = q.eq('team_id', profile.team_id);
            }

            const { data, error } = await q.order('created_at', { ascending: true });
            if (error) throw error;
            setChartLogs(data || []);
        } catch (err) {
            console.error("Fetch chart data error:", err);
        }
    };

    const fetchLogs = async (page = 1, isInitial = false) => {
        try {
            if (!isInitial) setLoadingMoreLogs(true);
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const isSuperAdmin = profile.access_level === 1 && !profile.team_id;
            const limit = 5;
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            let q = supabase
                .from('benchrex_logging')
                .select('*, personality:personalities(name)');

            if (!isSuperAdmin && profile.team_id) {
                q = q.eq('team_id', profile.team_id);
            }

            const { data, error } = await q
                .order('created_at', { ascending: false })
                .range(from, to);

            if (error) throw error;

            if (data) {
                if (isInitial) {
                    setLogs(data);
                } else {
                    setLogs(prev => [...prev, ...data]);
                }
                setHasMoreLogs(data.length === limit);
            }
        } catch (err) {
            console.error("Fetch logs error:", err);
            toast.error('Failed to fetch logs');
        } finally {
            if (!isInitial) setLoadingMoreLogs(false);
        }
    };

    const loadMoreLogs = () => {
        const nextPage = logsPage + 1;
        setLogsPage(nextPage);
        fetchLogs(nextPage);
    };



    const fetchUserConversations = useCallback(async (userId) => {
        setLoadingConvs(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const query = supabase
                .from('conversations')
                .select('*')
                .eq('user_id', userId)
                .eq('team_id', profile.team_id)
                .order('updated_at', { ascending: false });
            if (activeTab === 'expert_console') {
                query.eq('is_expert_session', true);
            }
            const { data, error } = await query;
            if (error) throw error;
            setUserConversations(data || []);
        } catch (err) {
            toast.error("Failed to fetch user history");
        } finally {
            setLoadingConvs(false);
        }
    }, [activeTab]);

    useEffect(() => {
        if (selectedUserId) {
            fetchUserConversations(selectedUserId);
        } else {
            setUserConversations([]);
        }
    }, [selectedUserId, activeTab]);

    useEffect(() => {
        if (targetConversationId && (activeTab === 'expert_console' || activeTab === 'conversations')) {
            const currentConv = queries.find(q => q.id === targetConversationId);
            if (currentConv && currentConv.expert_has_unread) {
                markAsRead(targetConversationId);
            }
        }
    }, [targetConversationId, activeTab, queries]);

    const markAsRead = useCallback(async (id) => {
        try {
            const { error } = await supabase
                .from('conversations')
                .update({ expert_has_unread: false })
                .eq('id', id);

            if (!error) {
                // Update local state immediately to break the loop in useEffect
                setQueries(prev => prev.map(q => q.id === id ? { ...q, expert_has_unread: false } : q));

                // Also update stats unread count
                setStats(prev => ({
                    ...prev,
                    queries: Math.max(0, prev.queries - 1)
                }));
            }
        } catch (err) {
            console.error("Failed to mark as read:", err);
        }
    }, []);

    const handleSavePersonality = async () => {
        try {
            if (!newPersonality.name || !newPersonality.system_instructions) {
                toast.error('Name and System Instructions are required');
                return;
            }

            const payload = { ...newPersonality };
            delete payload.provider;
            // Ensure model_id is used for new saves

            if (editingPersonality) {
                const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
                const { error } = await supabase
                    .from('personalities')
                    .update({ ...payload, team_id: profile.team_id })
                    .eq('id', editingPersonality.id);
                if (error) throw error;
                toast.success('Personality updated');
            } else {
                const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
                const personalityId = payload.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '-' + Math.random().toString(36).substring(2, 7);
                const { error } = await supabase
                    .from('personalities')
                    .insert([{ ...payload, id: personalityId, team_id: profile.team_id }]);
                if (error) throw error;
                toast.success('Personality created');
            }
            setIsDialogOpen(false);
            setEditingPersonality(null);
            setNewPersonality({
                name: '',
                model: '',
                model_id: models[0]?.id || '',
                system_instructions: '',
                description: '',
                icon: '🤖',
                tool_web_search: false,
                tool_code_interpreter: false,
                tool_image_gen: false,
                tool_calendar_mgmt: false,
                tool_chain_of_thought: false,
                api_key: '',
            });
            fetchData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDeletePersonality = async (id) => {
        if (!window.confirm('Are you sure you want to delete this personality?')) return;
        try {
            const { error } = await supabase.from('personalities').delete().eq('id', id);
            if (error) throw error;
            toast.success('Personality deleted');
            fetchData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleSaveUser = async () => {
        if (!newUser.name || !newUser.email) {
            toast.error('Name and Email are required');
            return;
        }

        try {
            if (editingUser) {
                // Update user in both systems via backend
                await api.put(`/api/benchrex/users/${editingUser.id}`, {
                    name: newUser.name,
                    email: newUser.email,
                    password: newUser.password,
                    role: newUser.role,
                    credits: newUser.credits,
                    grade: newUser.grade,
                    course: newUser.course,
                    batch: newUser.batch
                });

                toast.success('User updated and Auth synced');
            } else {
                // Call the backend registration API to sync with Supabase Auth
                const response = await api.post('/api/benchrex/register-user', {
                    name: newUser.name,
                    email: newUser.email,
                    password: newUser.password,
                    role: newUser.role,
                    credits: newUser.credits,
                    grade: newUser.grade,
                    course: newUser.course,
                    batch: newUser.batch
                });

                toast.success('User registered and Auth account created');
                await fetchData();
                setIsUserDialogOpen(false);
                setNewUser({ name: '', email: '', password: '', role: 'student', credits: 100, grade: '', course: '', batch: '' });
            }
        } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Failed to save user';
            toast.error(detail);
            console.error(err);
        }
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await api.delete(`/api/benchrex/users/${id}`);
            toast.success('User deleted and Auth synced');
            fetchData();
        } catch (err) {
            toast.error('Failed to delete user');
        }
    };

    const [systemHealth, setSystemHealth] = useState([]);
    const [isHealthOpen, setIsHealthOpen] = useState(false);
    const [healthLoading, setHealthLoading] = useState(false);
    const [teamName, setTeamName] = useState('');

    const fetchSystemHealth = async () => {
        try {
            setHealthLoading(true);
            const res = await api.get('/api/system/health');
            setSystemHealth(res.data || []);
        } catch (err) {
            console.error("Health check failed:", err);
        } finally {
            setHealthLoading(false);
        }
    };

    useEffect(() => {
        fetchSystemHealth();
        const interval = setInterval(fetchSystemHealth, 60000);
        return () => clearInterval(interval);
    }, []);

    const syncLegacyUsers = async () => {
        try {
            setLoading(true);
            const res = await api.post('/api/benchrex/sync-legacy-users');
            toast.success(`Successfully synced ${res.data.synced} users to Auth.`);
            if (res.data.errors.length > 0) {
                console.warn('Sync errors:', res.data.errors);
            }
            await fetchData();
        } catch (err) {
            toast.error('Sync failed');
        } finally {
            setLoading(false);
        }
    };

    const navItems = useMemo(() => {
        const items = [];
        if (level <= 2) items.push({ id: 'overview', label: 'Overview', icon: LayoutDashboard });
        if (level <= 8) items.push({ id: 'conversations', label: 'Conversations', icon: MessageSquare });
        if (level <= 10) items.push({ id: 'expert_console', label: 'Expert Console', icon: Terminal, badge: stats.queries > 0 ? stats.queries : null });
        if (level <= 2) items.push({ id: 'users', label: 'Manage Users', icon: Users });
        if (level <= 1) items.push({ id: 'personalities', label: 'AI Personas', icon: BrainCircuit });
        if (level <= 2) items.push({ id: 'logs', label: 'System Logs', icon: History });
        if (level <= 8) items.push({ id: 'feedback', label: 'Feedback', icon: HeartPulse });
        return items;
    }, [level, stats.queries]);

    useEffect(() => {
        if (navItems.length > 0) {
            const isAllowed = navItems.some(item => item.id === activeTab);
            if (!isAllowed) {
                setActiveTab(navItems[0].id);
            }
        }
    }, [activeTab, navItems, setActiveTab]);

    const tabDescriptions = {
        overview: 'Real-time intelligence metrics and cognitive performance metrics',
        conversations: 'Explore institutional AI chat sessions and user interactions',
        expert_console: 'Resolve complex escalations and manage human-in-the-loop expert handovers',
        users: 'Configure portal access levels, student profiles, and administrative permissions',
        personalities: 'Design, tune, and deploy specialized AI assistants and system prompts',
        logs: 'Audit trails, credits consumption histories, and backend processing logs',
        feedback: 'Analyze user sentiment, thumbs up/down logs, and feature requests'
    };

    const totalCostValue = useMemo(() => {
        return stats.total_cost.toFixed(4);
    }, [stats.total_cost]);

    return (
        <div className="flex-1 flex h-full bg-zinc-50 dark:bg-zinc-950 overflow-hidden font-body">
            {/* Sidebar */}
            {isMobile && mobileNavOpen && (
                <div 
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] animate-in fade-in duration-300"
                    onClick={() => setMobileNavOpen(false)}
                />
            )}
            <aside
                className={cn(
                    "flex flex-col border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 transition-all duration-300 ease-in-out z-[80]",
                    isMobile 
                        ? (mobileNavOpen ? "fixed inset-y-0 left-0 w-72 shadow-2xl translate-x-0" : "fixed inset-y-0 left-0 w-72 -translate-x-full")
                        : (sidebarCollapsed ? "w-20" : "w-64")
                )}
                style={{ willChange: 'width, transform' }}
            >
                <div className="p-6 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                    <div className={cn("flex items-center gap-3", sidebarCollapsed && !isMobile && "hidden")}>
                        <span className="font-display text-2xl font-black tracking-tighter uppercase dark:text-white">
                            Benchrex
                        </span>
                    </div>
                    {!isMobile && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        >
                            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                        </Button>
                    )}
                    {isMobile && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            onClick={() => setMobileNavOpen(false)}
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    )}
                </div>

                <nav className="flex-1 p-4 space-y-2">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => {
                                setActiveTab(item.id);
                                if (isMobile) setMobileNavOpen(false);
                            }}
                            className={cn(
                                "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group relative",
                                activeTab === item.id
                                    ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold"
                                    : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100"
                            )}
                        >
                            <item.icon className={cn("w-5 h-5 shrink-0", activeTab === item.id ? "text-blue-600" : "text-zinc-400 group-hover:text-zinc-600")} />
                            {(!sidebarCollapsed || isMobile) && (
                                <div className="flex-1 flex items-center justify-between">
                                    <span className="text-sm">{item.label}</span>
                                    {item.badge && (
                                        <Badge className="bg-blue-600 text-white border-none h-5 min-w-5 flex items-center justify-center text-[10px]">
                                            {item.badge}
                                        </Badge>
                                    )}
                                </div>
                            )}
                            {sidebarCollapsed && !isMobile && item.badge && (
                                <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-blue-600 border-2 border-white dark:border-zinc-900" />
                            )}
                        </button>
                    ))}
                </nav>

                {level <= 1 && (
                    <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
                        <button
                            onClick={() => {
                                setIsConfigOpen(true);
                                if (isMobile) setMobileNavOpen(false);
                            }}
                            className={cn(
                                "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all",
                                sidebarCollapsed && !isMobile && "justify-center"
                            )}
                        >
                            <Settings className="w-5 h-5" />
                            {(!sidebarCollapsed || isMobile) && <span className="text-sm font-medium">Settings</span>}
                        </button>
                    </div>
                )}
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col overflow-hidden relative">
                {/* Global Header */}
                <header className={cn(
                    "border-b border-zinc-200 dark:border-zinc-800 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl flex items-center justify-between sticky top-0 z-50 transition-all",
                    isMobile ? "h-16 px-4" : "h-24 px-8"
                )}>
                    <div className="flex items-center gap-4">
                        {isMobile && (
                            <button
                                onClick={() => setMobileSidebarOpen(true)}
                                className="w-10 h-10 flex items-center justify-center -ml-1 text-zinc-600 dark:text-zinc-400"
                            >
                                <ChevronsRight className="w-5 h-5" />
                            </button>
                        )}
                        <div className="flex flex-col gap-1">
                            <h2 className="font-display text-xl md:text-2xl font-black tracking-tighter dark:text-white uppercase leading-none">
                                {activeTab === 'overview' ? 'Dashboard Overview' : navItems.find(n => n.id === activeTab)?.label}
                            </h2>
                            <p className="text-[10px] md:text-[11px] text-zinc-500 font-bold uppercase tracking-[0.15em] opacity-80 mt-0.5">
                                {tabDescriptions[activeTab] || 'Operational workspace'}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 flex justify-center">
                    </div>

                    <div className="flex items-center gap-10">
                        {/* Compact Global Stats */}
                        <div className={cn(
                            "flex items-center gap-6 md:gap-10",
                            isMobile ? "overflow-x-auto no-scrollbar py-2 max-w-[50vw]" : "hidden xl:flex"
                        )}>
                            {[
                                { label: 'Students', value: stats.users, color: 'text-blue-500' },
                                { label: 'Personas', value: stats.personalities, color: 'text-purple-500' },
                                { label: 'Cost', value: `$${totalCostValue}`, color: 'text-orange-500' },
                                { label: 'Feedback', value: stats.feedback, color: 'text-green-500' }
                            ].map((stat, i) => (
                                <div key={i} className="flex flex-col items-start border-l border-dashed border-zinc-300 dark:border-zinc-700 pl-4 md:pl-5 h-8 md:h-10 justify-center shrink-0">
                                    <span className="text-[8px] md:text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-0.5 leading-none">{stat.label}</span>
                                    <span className={cn("text-sm md:text-xl font-black tracking-tighter leading-none", stat.color)}>
                                        {stat.value}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center gap-4 border-l border-zinc-200 dark:border-zinc-800 pl-8 h-10">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 w-9 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 transition-all hover:rotate-180 duration-500"
                                onClick={fetchData}
                            >
                                <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
                            </Button>
                        </div>
                    </div>
                </header>

                {/* Tab Content */}
                <div className="flex-1 overflow-hidden relative">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10, filter: 'blur(10px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            exit={{ opacity: 0, y: -10, filter: 'blur(10px)' }}
                            transition={{ duration: 0.2 }}
                            className="h-full"
                        >
                            {activeTab === 'overview' && (
                                <div className="h-full overflow-y-auto p-8 custom-scrollbar">
                                    <div className="max-w-7xl mx-auto space-y-12">
                                        {/* Data Visualization */}
                                        <div className="space-y-8">
                                            <div className={cn(
                                                    "bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden",
                                                    isMobile ? "p-4" : "p-8"
                                                )}>
                                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                                                                <BarChart3 className="w-5 h-5 text-blue-600" />
                                                            </div>
                                                            <h4 className="font-display text-base md:text-lg font-bold dark:text-white uppercase tracking-tight">Query Volume</h4>
                                                        </div>
                                                        <div className="flex gap-2 items-center overflow-x-auto pb-2 md:pb-0 no-scrollbar">
                                                            <Select value={chartHorizon} onValueChange={setChartHorizon}>
                                                                <SelectTrigger className="w-32 h-9 text-[10px] font-bold uppercase rounded-xl border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                                                                    <SelectValue placeholder="Horizon" />
                                                                </SelectTrigger>
                                                                <SelectContent className="rounded-xl border-zinc-200 dark:border-zinc-800">
                                                                    <SelectItem value="24h" className="text-[10px] font-bold uppercase">Last 24 Hours</SelectItem>
                                                                    <SelectItem value="7d" className="text-[10px] font-bold uppercase">Last 7 Days</SelectItem>
                                                                    <SelectItem value="30d" className="text-[10px] font-bold uppercase">Last 30 Days</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            {!isMobile && (
                                                                <Badge variant="outline" className="rounded-lg text-[10px] font-bold uppercase py-1 px-3 border-blue-500/30 text-blue-600 bg-blue-50 dark:bg-blue-900/10 whitespace-nowrap">
                                                                    {chartHorizon === '24h' ? 'Real-time Performance' : `Historical Volume (${chartHorizon})`}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={cn("w-full relative", isMobile ? "h-[250px]" : "h-[350px]")}>
                                                        <ResponsiveContainer width="100%" height="100%" debounce={200}>
                                                            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: isMobile ? -30 : -20, bottom: 0 }}>
                                                                <defs>
                                                                    <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                                                                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                                                                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                                                                    </linearGradient>
                                                                </defs>
                                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" opacity={0.5} />
                                                                <XAxis 
                                                                    dataKey="name" 
                                                                    axisLine={false} 
                                                                    tickLine={false} 
                                                                    tick={{ fontSize: 9, fill: '#94a3b8' }} 
                                                                    dy={10}
                                                                    interval="preserveStartEnd"
                                                                    minTickGap={isMobile ? 50 : 30}
                                                                />
                                                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#94a3b8' }} />
                                                                <RechartsTooltip
                                                                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '11px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                                                                    itemStyle={{ color: '#60a5fa', fontWeight: 'bold' }}
                                                                    cursor={{ stroke: '#2563eb', strokeWidth: 1, strokeDasharray: '5 5' }}
                                                                />
                                                                <Area 
                                                                    type="monotone" 
                                                                    dataKey="volume" 
                                                                    stroke="#2563eb" 
                                                                    strokeWidth={3} 
                                                                    fillOpacity={1} 
                                                                    fill="url(#colorVolume)" 
                                                                    animationDuration={1000}
                                                                    isAnimationActive={!isMobile}
                                                                />
                                                            </AreaChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
 
                                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                                    <div className="lg:col-span-2 space-y-8">
                                                        {/* Student Requests Section */}
                                                        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden p-6">
                                                            <div className="flex items-center justify-between mb-6">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                                                                        <MessageSquare className="w-5 h-5 text-orange-600" />
                                                                    </div>
                                                                    <h4 className="font-display text-base md:text-lg font-bold dark:text-white uppercase tracking-tight">Recent Student Requests</h4>
                                                                </div>
                                                                <Button variant="ghost" size="sm" className="text-[10px] font-bold uppercase tracking-widest text-blue-600 h-9 px-4 rounded-xl hover:bg-blue-50" onClick={() => setActiveTab('expert_console')}>View All</Button>
                                                            </div>
                                                        <div className="space-y-3">
                                                            {queries.filter(q => q.is_expert_session).slice(0, 5).map(q => (
                                                                <div key={q.id} className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-800 group hover:border-blue-500/30 transition-all">
                                                                    <div className="flex items-center gap-4">
                                                                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 font-bold text-[10px]">
                                                                            {q.user?.name?.charAt(0) || 'S'}
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100 whitespace-normal break-words leading-tight">{q.title || 'Inquiry regarding course material'}</p>
                                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                                <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">{q.user?.name || 'Anonymous Student'}</p>
                                                                                <span className="text-zinc-300 dark:text-zinc-700">•</span>
                                                                                <p className="text-[10px] text-zinc-400 font-medium uppercase tracking-wider">{new Date(q.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                                                {q.expert_id ? (
                                                                                    <Badge className="bg-green-500 hover:bg-green-600 text-white text-[8px] h-3 px-1 border-none ml-1">Online</Badge>
                                                                                ) : q.is_expert_session ? (
                                                                                    <Badge className="bg-orange-500 hover:bg-orange-600 text-white text-[8px] h-3 px-1 border-none ml-1">Expert</Badge>
                                                                                ) : null}
                                                                                {q.expert_has_unread && (
                                                                                    <Badge className="bg-blue-500 hover:bg-blue-600 text-white text-[8px] h-3 px-1 border-none ml-1">New</Badge>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        onClick={() => {
                                                                            setTargetConversationId(q.id);
                                                                            setActiveTab('expert_console');
                                                                        }}
                                                                    >
                                                                        <ChevronRight className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                            {queries.length === 0 && (
                                                                <div className="py-12 text-center border-2 border-dashed border-zinc-100 dark:border-zinc-800 rounded-3xl">
                                                                    <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest">No active requests</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>


                                </div>
                                            </div>
                                        </div>
                                    </div>
                            )}

                            {(activeTab === "conversations" || activeTab === "expert_console") && (
                                <div className="flex h-full overflow-hidden bg-white dark:bg-zinc-950 relative">
                                    {/* Console Sidebar */}
                                    <aside className={cn(
                                        "border-r border-zinc-200 dark:border-zinc-800 flex flex-col bg-zinc-50/30 dark:bg-zinc-900/30 transition-all duration-300 z-30",
                                        isMobile 
                                            ? (consoleSidebarOpen ? "fixed inset-y-0 left-0 w-[85%] shadow-2xl translate-x-0" : "fixed inset-y-0 left-0 w-[85%] -translate-x-full")
                                            : (consoleSidebarOpen ? "w-80" : "w-0 overflow-hidden")
                                    )}>
                                        <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                                                {selectedUserId ? 'User History' : (sidebarMode === 'users' ? 'All Users' : 'Recent Threads')}
                                            </h3>
                                            {isMobile && (
                                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setConsoleSidebarOpen(false)}>
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                        
                                        <div className="p-4 space-y-4">
                                            <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
                                                <button
                                                    onClick={() => {
                                                        setSidebarMode('threads');
                                                        setSelectedUserId(null);
                                                    }}
                                                    className={cn(
                                                        "flex-1 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                                                        sidebarMode === 'threads' && !selectedUserId
                                                            ? "bg-white dark:bg-zinc-900 text-blue-600 shadow-sm"
                                                            : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
                                                    )}
                                                >
                                                    Threads
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSidebarMode('users');
                                                        setSelectedUserId(null);
                                                    }}
                                                    className={cn(
                                                        "flex-1 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                                                        sidebarMode === 'users'
                                                            ? "bg-white dark:bg-zinc-900 text-blue-600 shadow-sm"
                                                            : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
                                                    )}
                                                >
                                                    Users
                                                </button>
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                {selectedUserId && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-9 w-9 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-blue-600 shrink-0"
                                                        onClick={() => {
                                                            setSelectedUserId(null);
                                                            setTargetConversationId(null);
                                                            setSidebarMode('users');
                                                        }}
                                                    >
                                                        <ChevronLeft className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <div className="relative flex-1">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                                                    <Input
                                                        placeholder={selectedUserId ? "Filter..." : "Search..."}
                                                        className="pl-9 h-10 rounded-xl text-xs bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
                                            {sidebarMode === 'threads' || selectedUserId ? (
                                                <>
                                                    {loadingConvs ? (
                                                        <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" /></div>
                                                    ) : (
                                                        (selectedUserId ? userConversations :
                                                            activeTab === 'expert_console' ? queries.filter(q => q.is_expert_session) : queries
                                                        ).filter(c =>
                                                            c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                            c.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
                                                        ).map(conv => (
                                                            <button
                                                                key={conv.id}
                                                                onClick={() => {
                                                                    setSelectedUserId(conv.user_id);
                                                                    setTargetConversationId(conv.id);
                                                                    if (isMobile) setConsoleSidebarOpen(false);
                                                                }}
                                                                className={cn(
                                                                    "w-full text-left p-4 rounded-2xl transition-all group relative border border-transparent mb-1",
                                                                    targetConversationId === conv.id
                                                                        ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20 border-blue-400"
                                                                        : "hover:bg-white dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-200 dark:hover:border-zinc-700"
                                                                )}
                                                            >
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <p className="text-xs font-bold line-clamp-1 flex-1">{conv.title || 'Untitled Session'}</p>
                                                                    {conv.expert_id ? (
                                                                        <Badge className="ml-2 bg-green-500 hover:bg-green-600 text-white text-[8px] h-3 px-1 border-none shrink-0">Online</Badge>
                                                                    ) : conv.is_expert_session ? (
                                                                        <Badge className="ml-2 bg-orange-500 hover:bg-orange-600 text-white text-[8px] h-3 px-1 border-none shrink-0">Expert</Badge>
                                                                    ) : null}
                                                                </div>
                                                                <div className="flex items-center justify-between opacity-70">
                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                        <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-[8px] font-black text-blue-600">
                                                                            {conv.user?.name?.charAt(0) || 'U'}
                                                                        </div>
                                                                        <span className="text-[10px] font-black uppercase tracking-widest truncate max-w-[120px]">
                                                                            {conv.user?.name || 'Anonymous'}
                                                                        </span>
                                                                    </div>
                                                                    <span className="text-[9px] font-medium uppercase tracking-widest shrink-0">
                                                                        {new Date(conv.updated_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                                                    </span>
                                                                </div>
                                                            </button>
                                                        ))
                                                    )}
                                                </>
                                            ) : (
                                                <div className="grid grid-cols-1 gap-3 p-1">
                                                    {users.filter(u =>
                                                        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
                                                    ).map(u => (
                                                        <button
                                                            key={u.id}
                                                            onClick={() => {
                                                                setSelectedUserId(u.id);
                                                                setSidebarMode('threads');
                                                            }}
                                                            className="flex flex-col p-4 rounded-2xl bg-white dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 hover:border-blue-500/50 hover:shadow-lg transition-all group text-left relative overflow-hidden"
                                                        >
                                                            <div className="flex items-center gap-3 relative z-10">
                                                                <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 font-black text-sm group-hover:scale-110 transition-transform">
                                                                    {u.name?.charAt(0) || u.email?.charAt(0)}
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="text-xs font-black text-zinc-800 dark:text-zinc-100 truncate tracking-tight">{u.name}</p>
                                                                    <p className="text-[9px] text-zinc-400 font-black uppercase tracking-widest truncate mt-0.5">{u.role}</p>
                                                                </div>
                                                                <ChevronRight className="w-4 h-4 text-zinc-300 opacity-0 group-hover:opacity-100 transition-all" />
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </aside>

                                    {/* Main Console Area */}
                                    <div className="flex-1 flex flex-col relative overflow-hidden bg-white dark:bg-zinc-950">
                                        {/* Mobile Sidebar Toggle */}
                                        {isMobile && !consoleSidebarOpen && (
                                            <button 
                                                onClick={() => setConsoleSidebarOpen(true)}
                                                className="absolute left-4 top-4 z-40 w-10 h-10 rounded-xl bg-blue-600 text-white shadow-lg flex items-center justify-center animate-in zoom-in"
                                            >
                                                <Users className="w-5 h-5" />
                                            </button>
                                        )}

                                        {targetConversationId ? (
                                            <QueryClientProvider client={queryClient}>
                                                <TooltipProvider>
                                                    <ScholarsAnchorProvider
                                                        teamId={JSON.parse(localStorage.getItem('admin_profile') || '{}').team_id}
                                                        userId={selectedUserId}
                                                        forceConversationId={targetConversationId}
                                                        isAdminView={true}
                                                        forcedDarkMode={darkMode}
                                                        onDarkModeChange={setGlobalDarkMode}
                                                    >
                                                        <React.Suspense fallback={<div className="flex h-full items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>}>
                                                            <ChatPage isEmbedded={true} />
                                                        </React.Suspense>
                                                    </ScholarsAnchorProvider>
                                                </TooltipProvider>
                                            </QueryClientProvider>
                                        ) : (
                                            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-zinc-50/50 dark:bg-zinc-900/50">
                                                <div className="w-20 h-20 rounded-[2.5rem] bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-6 shadow-inner">
                                                    <Terminal className="w-10 h-10 text-zinc-300" />
                                                </div>
                                                <h3 className="text-xl font-black tracking-tighter uppercase mb-2 dark:text-white">
                                                    {selectedUserId ? 'Select a Session' : (sidebarMode === 'users' ? 'Select a Student' : 'Select a Conversation')}
                                                </h3>
                                                <p className="text-sm text-zinc-400 font-medium max-w-xs leading-relaxed italic">
                                                    {selectedUserId
                                                        ? 'Browse through the user\'s conversation history to review AI interactions and expert interventions.'
                                                        : (sidebarMode === 'users'
                                                            ? 'Choose a student from the directory in the sidebar to inspect their learning journey.'
                                                            : 'Select a thread from the history to view the full interaction logs.')}
                                                </p>
                                                {isMobile && (
                                                    <Button 
                                                        className="mt-8 rounded-2xl bg-blue-600 text-white font-bold h-12 px-8"
                                                        onClick={() => setConsoleSidebarOpen(true)}
                                                    >
                                                        Browse Sessions
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Overlay for mobile sidebar */}
                                    {isMobile && consoleSidebarOpen && (
                                        <div 
                                            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 animate-in fade-in"
                                            onClick={() => setConsoleSidebarOpen(false)}
                                        />
                                    )}
                                </div>
                            )}

                            {activeTab === 'personalities' && (
                                <div className="h-full overflow-y-auto p-8 custom-scrollbar">
                                    <div className="max-w-7xl mx-auto space-y-8">
                                        <div className="flex flex-col md:flex-row md:items-center justify-end gap-4">
                                            {/* AI Personalities Heading moved to Header */}
                                            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                                                <div className="relative flex-1 md:w-64">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                                    <Input
                                                        placeholder="Filter instructors..."
                                                        className="pl-10 h-12 bg-white dark:bg-zinc-900 rounded-2xl w-full shadow-sm border-zinc-200 dark:border-zinc-800 font-bold"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                </div>
                                                <Button className="rounded-2xl h-12 bg-zinc-900 dark:bg-white dark:text-zinc-900 font-black text-xs uppercase tracking-widest gap-2 px-6 shadow-xl" onClick={() => {
                                                    setEditingPersonality(null);
                                                    setNewPersonality({
                                                        name: '',
                                                        model_id: models[0]?.id || '',
                                                        model: models[0]?.name || 'llama-3.3-70b-versatile',
                                                        system_instructions: '',
                                                        description: '',
                                                        category: 'general',
                                                        icon: '🤖',
                                                        tool_web_search: false,
                                                        tool_code_interpreter: false,
                                                        tool_image_gen: false,
                                                        tool_calendar_mgmt: false,
                                                        tool_chain_of_thought: false,
                                                        api_key: '',
                                                    });
                                                    setIsDialogOpen(true);
                                                }}>
                                                    <Plus className="w-4 h-4" /> New Instructor
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                            {personalities.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())).map(p => (
                                                <div key={p.id} className="bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-zinc-800 shadow-sm flex flex-col group hover:shadow-xl hover:-translate-y-1 transition-all">
                                                    <div className="p-6 flex-1 space-y-6">
                                                        <div className="flex items-center justify-between">
                                                            <div className="w-16 h-16 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-4xl shadow-inner group-hover:scale-110 transition-transform">
                                                                {p.icon}
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <Button size="icon" variant="ghost" className="h-10 w-10 rounded-xl" onClick={() => {
                                                                    setEditingPersonality(p);
                                                                    setNewPersonality({ ...p, model_id: p.model_id || '', model: p.model || '' });
                                                                    setIsDialogOpen(true);
                                                                }}>
                                                                    <Edit3 className="w-4 h-4" />
                                                                </Button>
                                                                <Button size="icon" variant="ghost" className="h-10 w-10 rounded-xl text-red-500 hover:bg-red-50" onClick={() => handleDeletePersonality(p.id)}>
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        </div>

                                                        <div className="space-y-1">
                                                            <h4 className="text-xl font-bold dark:text-white tracking-tight">{p.name}</h4>
                                                            <p className="text-xs font-bold text-blue-600 dark:text-blue-400 font-mono uppercase tracking-widest">
                                                                {models.find(m => m.id === p.model_id)?.name || p.model || 'Unknown Model'}
                                                            </p>
                                                        </div>

                                                        <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-3 leading-relaxed font-medium italic">
                                                            "{p.description || 'Dedicated AI instructor for ScholarsAnchor students.'}"
                                                        </p>

                                                        <div className="pt-4 border-t border-zinc-50 dark:border-zinc-800">
                                                            <div className="flex flex-wrap gap-2">
                                                                {p.tool_web_search && <Badge className="rounded-lg bg-blue-50 text-blue-600 border-none font-bold text-[9px] uppercase px-2 py-1">Web Search</Badge>}
                                                                {p.tool_code_interpreter && <Badge className="rounded-lg bg-green-50 text-green-600 border-none font-bold text-[9px] uppercase px-2 py-1">Code</Badge>}
                                                                {p.tool_calendar_mgmt && <Badge className="rounded-lg bg-red-50 text-red-600 border-none font-bold text-[9px] uppercase px-2 py-1">Scheduler</Badge>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'logs' && (
                                <div className="h-full overflow-y-auto p-8 custom-scrollbar">
                                    <div className="max-w-7xl mx-auto space-y-8">
                                        <div className="flex items-center justify-end">
                                            {/* System Logs Heading moved to Header */}
                                            <div className="flex items-center gap-4 px-6 py-3 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm">
                                                <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Total API Cost</span>
                                                <span className="text-lg font-black text-zinc-900 dark:text-white">${totalCostValue}</span>
                                            </div>
                                        </div>

                                        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-zinc-800 shadow-xl overflow-hidden mb-8">
                                            <div className="overflow-x-auto">
                                                {!isMobile ? (
                                                    <table className="w-full text-left border-collapse">
                                                        <thead>
                                                            <tr className="bg-zinc-50/50 dark:bg-zinc-800/30 border-b border-zinc-100 dark:border-zinc-800 font-display text-[10px] uppercase tracking-widest text-zinc-400">
                                                                <th className="px-6 py-5 font-bold">Timestamp</th>
                                                                <th className="px-6 py-5 font-bold">Instructor</th>
                                                                <th className="px-6 py-5 font-bold">Status</th>
                                                                <th className="px-6 py-5 font-bold">Inference Cost</th>
                                                                <th className="px-6 py-5 font-bold text-right">Details</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800 font-mono text-[11px]">
                                                            {logs.map(log => (
                                                                <tr key={log.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20 transition-colors">
                                                                    <td className="px-6 py-4 text-zinc-400">{new Date(log.created_at).toLocaleString()}</td>
                                                                    <td className="px-6 py-4 font-bold text-zinc-800 dark:text-zinc-200">{log.personality?.name || 'System'}</td>
                                                                    <td className="px-6 py-4">
                                                                        <Badge className={cn(
                                                                            "text-[9px] font-bold border-none px-2 py-0.5",
                                                                            log.status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                                        )}>
                                                                            {log.status.toUpperCase()}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="px-6 py-4 font-bold text-zinc-500">${parseFloat(log.cost).toFixed(4)}</td>
                                                                    <td className="px-6 py-4 text-right">
                                                                        <Button variant="ghost" size="sm" className="h-8 rounded-lg text-blue-600 font-bold hover:bg-blue-50" onClick={() => setTargetConversationId(log.conversation_id)}>View</Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="p-4 space-y-4">
                                                        {logs.map(log => (
                                                            <div key={log.id} className="p-5 rounded-3xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-800">
                                                                <div className="flex items-center justify-between mb-4">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-1">{new Date(log.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                                        <span className="text-sm font-black text-zinc-800 dark:text-zinc-100">{log.personality?.name || 'System'}</span>
                                                                    </div>
                                                                    <Badge className={cn(
                                                                        "text-[9px] font-black border-none px-3 py-1 rounded-xl shadow-sm",
                                                                        log.status === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                                                                    )}>
                                                                        {log.status.toUpperCase()}
                                                                    </Badge>
                                                                </div>

                                                                <div className="flex items-center justify-between p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 mb-4">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[8px] font-black text-zinc-400 uppercase tracking-widest mb-0.5">Inference Cost</span>
                                                                        <span className="text-sm font-black text-blue-600">${parseFloat(log.cost).toFixed(4)}</span>
                                                                    </div>
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm" 
                                                                        className="h-10 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-blue-600 font-black text-[10px] uppercase tracking-widest px-4"
                                                                        onClick={() => {
                                                                            setTargetConversationId(log.conversation_id);
                                                                            setActiveTab('conversations');
                                                                        }}
                                                                    >
                                                                        Inspect
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {logs.length === 0 && (
                                                    <div className="p-12 text-center text-zinc-500 font-bold uppercase tracking-widest text-[10px]">No logs available</div>
                                                )}
                                            </div>
                                        </div>

                                        {hasMoreLogs && (
                                            <div className="flex justify-center pb-8">
                                                <Button
                                                    onClick={loadMoreLogs}
                                                    disabled={loadingMoreLogs}
                                                    className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white border border-zinc-200 dark:border-zinc-800 rounded-2xl px-8 h-12 font-bold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all shadow-lg"
                                                >
                                                    {loadingMoreLogs ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Optimizing Neural Feed...
                                                        </>
                                                    ) : (
                                                        'Load More Neural Logs'
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'users' && (
                                <div className="h-full overflow-y-auto p-8 custom-scrollbar">
                                    <div className="max-w-7xl mx-auto space-y-8">
                                        <div className="flex items-center justify-end">
                                            {/* User Management Heading moved to Header */}
                                            <div className="flex items-center gap-2">

                                                <Button
                                                    variant="outline"
                                                    className="border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300 gap-2 rounded-2xl h-12 px-6 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all font-bold"
                                                    onClick={() => setBulkImportOpen(true)}
                                                >
                                                    <Upload className="w-4 h-4" /> Bulk Import
                                                </Button>
                                                <Button
                                                    className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 gap-2 rounded-2xl h-12 px-6 shadow-xl hover:scale-105 active:scale-95 transition-all font-bold"
                                                    onClick={() => { setEditingUser(null); setNewUser({ name: '', email: '', role: 'student', credits: 100, grade: '', course: '', batch: '', password: '' }); setIsUserDialogOpen(true); }}
                                                >
                                                    <UserPlus className="w-4 h-4" /> Add User
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="bg-white dark:bg-zinc-900 rounded-[2.5rem] border border-zinc-200 dark:border-zinc-800 shadow-xl overflow-hidden">
                                            <div className="p-6 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-4">
                                                <div className="relative flex-1">
                                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                                    <Input
                                                        placeholder="Search users by name or email..."
                                                        className="pl-11 h-12 rounded-2xl bg-zinc-50 dark:bg-zinc-800 border-none dark:text-zinc-100 dark:placeholder:text-zinc-500"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="overflow-x-auto">
                                                {!isMobile ? (
                                                    <table className="w-full text-left">
                                                        <thead>
                                                            <tr className="bg-zinc-50 dark:bg-zinc-800/50">
                                                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-400">User Details</th>
                                                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-400">Role</th>
                                                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-400">Credits</th>
                                                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-400">Joined</th>
                                                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-400 text-right">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                                                            {users.filter(u =>
                                                                u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                                u.email?.toLowerCase().includes(searchTerm.toLowerCase())
                                                            ).map((user) => (
                                                                <tr key={user.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-800/30 transition-colors group">
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-bold text-xs">
                                                                                {user.name?.charAt(0) || user.email?.charAt(0)}
                                                                            </div>
                                                                            <div>
                                                                                <p className="text-sm font-bold text-zinc-900 dark:text-zinc-50">{user.name}</p>
                                                                                <p className="text-[10px] text-zinc-500 font-medium">{user.email}</p>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <Badge variant="outline" className="rounded-lg text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                                                                            {user.role}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-xs font-mono font-bold text-zinc-600 dark:text-zinc-400">{user.credits}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[10px] text-zinc-400 font-medium">
                                                                            {new Date(user.created_at).toLocaleDateString()}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-right">
                                                                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-zinc-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20" onClick={() => { setEditingUser(user); setNewUser(user); setIsUserDialogOpen(true); }}>
                                                                                <Edit3 className="w-3.5 h-3.5" />
                                                                            </Button>
                                                                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => handleDeleteUser(user.id)}>
                                                                                <Trash2 className="w-3.5 h-3.5" />
                                                                            </Button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="p-4 space-y-4">
                                                        {users.filter(u =>
                                                            u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                            u.email?.toLowerCase().includes(searchTerm.toLowerCase())
                                                        ).map((user) => (
                                                            <div key={user.id} className="p-5 rounded-3xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-800">
                                                                <div className="flex items-start justify-between mb-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-12 h-12 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-black text-sm">
                                                                            {user.name?.charAt(0) || user.email?.charAt(0)}
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-sm font-black text-zinc-900 dark:text-zinc-50">{user.name}</p>
                                                                            <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">{user.email}</p>
                                                                        </div>
                                                                    </div>
                                                                    <Badge variant="outline" className="rounded-xl text-[9px] font-black uppercase tracking-widest px-3 py-1 bg-white dark:bg-zinc-900">
                                                                        {user.role}
                                                                    </Badge>
                                                                </div>
                                                                
                                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                                    <div className="p-3 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800">
                                                                        <p className="text-[8px] font-black text-zinc-400 uppercase tracking-widest mb-1">Credits</p>
                                                                        <p className="text-sm font-black text-blue-600">{user.credits}</p>
                                                                    </div>
                                                                    <div className="p-3 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800">
                                                                        <p className="text-[8px] font-black text-zinc-400 uppercase tracking-widest mb-1">Joined</p>
                                                                        <p className="text-sm font-black text-zinc-600 dark:text-zinc-300">
                                                                            {new Date(user.created_at).toLocaleDateString([], { month: 'short', year: 'numeric' })}
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                <div className="flex gap-2">
                                                                    <Button 
                                                                        className="flex-1 rounded-2xl h-11 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 font-black text-xs uppercase tracking-widest"
                                                                        onClick={() => { setEditingUser(user); setNewUser(user); setIsUserDialogOpen(true); }}
                                                                    >
                                                                        Edit Profile
                                                                    </Button>
                                                                    <Button 
                                                                        variant="outline" 
                                                                        className="w-11 h-11 p-0 rounded-2xl border-zinc-200 dark:border-zinc-800 text-red-500"
                                                                        onClick={() => handleDeleteUser(user.id)}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {users.length === 0 && (
                                                    <div className="p-12 text-center">
                                                        <Users className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                                                        <p className="text-sm text-zinc-500 font-medium">No users found</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}


                            {activeTab === 'feedback' && (
                                <div className="h-full overflow-y-auto p-8 custom-scrollbar">
                                    <div className="max-w-7xl mx-auto space-y-8">
                                        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-zinc-800 shadow-xl overflow-hidden">
                                            <div className="overflow-x-auto">
                                                {!isMobile ? (
                                                    <table className="w-full text-left border-collapse">
                                                        <thead>
                                                            <tr className="bg-zinc-50/50 dark:bg-zinc-800/30 border-b border-zinc-100 dark:border-zinc-800 font-display text-[10px] uppercase tracking-widest text-zinc-400">
                                                                <th className="px-6 py-5 font-bold">User</th>
                                                                <th className="px-6 py-5 font-bold">Rating</th>
                                                                <th className="px-6 py-5 font-bold">Feedback</th>
                                                                <th className="px-6 py-5 font-bold">Context</th>
                                                                <th className="px-6 py-5 font-bold text-right">Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800 text-[11px]">
                                                            {feedback.map(f => (
                                                                <tr key={f.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20 transition-colors">
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex flex-col">
                                                                            <span className="font-bold text-zinc-800 dark:text-zinc-200">{f.user?.name || 'Anonymous'}</span>
                                                                            <span className="text-[10px] text-zinc-500">{f.user?.email || ''}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className={cn(
                                                                            "w-8 h-8 rounded-lg flex items-center justify-center",
                                                                            f.rating === 1 ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                                                                        )}>
                                                                            {f.rating === 1 ? <ThumbsUp className="w-4 h-4" /> : <ThumbsDown className="w-4 h-4" />}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 max-w-xs">
                                                                        <p className="text-zinc-700 dark:text-zinc-300 line-clamp-2">{f.feedback_text || '-'}</p>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex flex-col gap-1 max-w-sm">
                                                                            <div className="flex items-center gap-1">
                                                                                <Badge variant="outline" className="text-[8px] h-3 px-1 border-zinc-200">PROMPT</Badge>
                                                                                <span className="text-zinc-500 truncate">{f.user_prompt || 'N/A'}</span>
                                                                            </div>
                                                                            <div className="flex items-center gap-1">
                                                                                <Badge variant="outline" className="text-[8px] h-3 px-1 border-blue-200 text-blue-600">AI</Badge>
                                                                                <span className="text-zinc-500 truncate">{f.ai_response || 'N/A'}</span>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-right text-zinc-400">
                                                                        {new Date(f.created_at).toLocaleDateString()}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="p-4 space-y-4">
                                                        {feedback.map(f => (
                                                            <div key={f.id} className="p-5 rounded-3xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-800">
                                                                <div className="flex items-center justify-between mb-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-bold text-[10px]">
                                                                            {f.user?.name?.charAt(0) || 'A'}
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-xs font-black text-zinc-900 dark:text-zinc-50">{f.user?.name || 'Anonymous'}</p>
                                                                            <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider">{new Date(f.created_at).toLocaleDateString()}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className={cn(
                                                                        "w-10 h-10 rounded-xl flex items-center justify-center shadow-sm",
                                                                        f.rating === 1 ? "bg-green-500 text-white" : "bg-red-500 text-white"
                                                                    )}>
                                                                        {f.rating === 1 ? <ThumbsUp className="w-4 h-4" /> : <ThumbsDown className="w-4 h-4" />}
                                                                    </div>
                                                                </div>

                                                                {f.feedback_text && (
                                                                    <div className="mb-4 p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800">
                                                                        <p className="text-xs text-zinc-700 dark:text-zinc-300 italic leading-relaxed">"{f.feedback_text}"</p>
                                                                    </div>
                                                                )}

                                                                <div className="space-y-2">
                                                                    <div className="flex flex-col gap-1 p-3 rounded-xl bg-zinc-100 dark:bg-zinc-800/50">
                                                                        <span className="text-[8px] font-black text-zinc-400 uppercase tracking-[0.2em]">User Query</span>
                                                                        <p className="text-[10px] text-zinc-600 dark:text-zinc-400 line-clamp-2">{f.user_prompt || 'No prompt content'}</p>
                                                                    </div>
                                                                    <div className="flex flex-col gap-1 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100/50 dark:border-blue-900/20">
                                                                        <span className="text-[8px] font-black text-blue-400 uppercase tracking-[0.2em]">AI Response</span>
                                                                        <p className="text-[10px] text-zinc-600 dark:text-zinc-400 line-clamp-2">{f.ai_response || 'No response content'}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {feedback.length === 0 && (
                                                    <div className="p-12 text-center text-zinc-500 font-bold uppercase tracking-widest text-[10px]">No feedback received</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>

            {/* Dialogs and Modals */}
            {/* User Dialog */}
            <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                <DialogContent className="w-[95vw] sm:max-w-[600px] rounded-[2.5rem] md:rounded-[3.5rem] p-0 overflow-hidden border-none shadow-2xl font-body bg-white dark:bg-zinc-900 max-h-[95vh] overflow-y-auto" aria-describedby="user-dialog-description">
                    <DialogDescription id="user-dialog-description" className="sr-only">Student profile configuration and role management portal for ScholarsAnchor</DialogDescription>
                    <div className="p-6 md:p-10 pb-6">
                        <DialogHeader>
                            <div className="flex items-center gap-4 mb-2">
                                <div className="w-12 h-12 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 shadow-inner">
                                    <Users className="w-6 h-6" />
                                </div>
                                <div>
                                    <DialogTitle className="font-display text-xl md:text-2xl font-black tracking-tighter uppercase dark:text-white">
                                        {editingUser ? 'Update User' : 'Register New User'}
                                    </DialogTitle>
                                    <DialogDescription id="user-dialog-description" className="text-[10px] md:text-xs text-zinc-500 font-medium tracking-wide">
                                        Configure portal access and student profile for the ScholarsAnchor ecosystem.
                                    </DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 py-6 md:py-8">
                            <div className="space-y-4 md:space-y-6">
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Full Name</Label>
                                    <Input
                                        placeholder="Student Name"
                                        value={newUser.name}
                                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                        className="h-12 rounded-2xl font-bold bg-zinc-50 dark:bg-zinc-800 border-none dark:text-zinc-100 dark:placeholder:text-zinc-500"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Email Address</Label>
                                    <Input
                                        type="email"
                                        placeholder="student@example.com"
                                        value={newUser.email}
                                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                        className="h-12 rounded-2xl font-bold bg-zinc-50 dark:bg-zinc-800 border-none dark:text-zinc-100 dark:placeholder:text-zinc-500"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Portal Role</Label>
                                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                                        <SelectTrigger className="h-12 rounded-2xl font-bold bg-zinc-50 dark:bg-zinc-800 border-none dark:text-zinc-100">
                                            <SelectValue placeholder="Select Role" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="student">Student</SelectItem>
                                            <SelectItem value="expert">Expert Instructor</SelectItem>
                                            <SelectItem value="admin">System Administrator</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Portal Password</Label>
                                    <Input
                                        type="password"
                                        placeholder="••••••••"
                                        value={newUser.password || ''}
                                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                        className="h-12 rounded-2xl font-bold bg-zinc-50 dark:bg-zinc-800 border-none dark:text-zinc-100 dark:placeholder:text-zinc-500"
                                    />
                                </div>
                            </div>

                            <div className="space-y-4 md:space-y-6">
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Enrolled Batch</Label>
                                    <Select value={newUser.batch} onValueChange={(v) => {
                                        const course = batches.find(b => b.name === v);
                                        setNewUser({
                                            ...newUser,
                                            batch: v,
                                            grade: course?.grade || ''
                                        });
                                    }}>
                                        <SelectTrigger className="h-12 rounded-2xl font-bold bg-zinc-50 dark:bg-zinc-800 border-none dark:text-zinc-100">
                                            <SelectValue placeholder="Select Batch" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {batches.map(b => (
                                                <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                                            ))}
                                            {batches.length === 0 && <SelectItem disabled value="none">No Batches Available</SelectItem>}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Credits Allowance</Label>
                                    <Input
                                        type="number"
                                        value={newUser.credits}
                                        onChange={(e) => setNewUser({ ...newUser, credits: parseInt(e.target.value) })}
                                        className="h-12 rounded-2xl font-bold bg-zinc-50 dark:bg-zinc-800 border-none dark:text-zinc-100 dark:placeholder:text-zinc-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="p-6 md:p-8 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-100 dark:border-zinc-800 flex flex-row gap-4 items-center">
                        <Button variant="ghost" className="flex-1 rounded-2xl h-14 font-bold dark:text-zinc-400 dark:hover:bg-zinc-800" onClick={() => setIsUserDialogOpen(false)}>Abort</Button>
                        <Button className="flex-[2] rounded-2xl h-14 bg-zinc-900 dark:bg-white dark:text-zinc-900 font-black uppercase tracking-widest shadow-xl" onClick={handleSaveUser}>
                            {editingUser ? 'Update User' : 'Register User'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="w-[95vw] sm:max-w-2xl bg-white dark:bg-zinc-900 rounded-[2.5rem] border-none shadow-2xl p-0 overflow-hidden font-body max-h-[95vh] overflow-y-auto" aria-describedby="personality-dialog-description">
                    <DialogHeader className="p-6 md:p-8 pb-0">
                        <DialogTitle className="font-display text-xl md:text-2xl font-black tracking-tighter uppercase text-zinc-900 dark:text-zinc-50">
                            {editingPersonality ? 'Refine Instructor' : 'Commission Instructor'}
                        </DialogTitle>
                        <DialogDescription id="personality-dialog-description" className="text-xs md:text-sm text-zinc-500 dark:text-zinc-400">
                            Define the intelligence and behavior of the ScholarsAnchor assistant.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="p-6 md:p-8 space-y-6 md:space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                            <div className="space-y-2">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Identity</Label>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="w-20 h-20 bg-zinc-100 dark:bg-zinc-800 rounded-[2rem] flex items-center justify-center text-4xl shadow-inner cursor-pointer hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors shrink-0 mx-auto sm:mx-0">
                                        <input
                                            type="text"
                                            value={newPersonality.icon}
                                            onChange={(e) => setNewPersonality({ ...newPersonality, icon: e.target.value })}
                                            className="w-full h-full bg-transparent text-center border-none focus:ring-0 dark:text-white"
                                        />
                                    </div>
                                    <div className="flex-1 space-y-3">
                                        <div className="grid grid-cols-1 gap-3">
                                            <div className="space-y-1">
                                                <Label className="text-[9px] font-black uppercase tracking-tighter text-zinc-400">Instructor Name</Label>
                                                <Input
                                                    placeholder="Instructor Name"
                                                    value={newPersonality.name}
                                                    onChange={(e) => setNewPersonality({ ...newPersonality, name: e.target.value })}
                                                    className="h-11 rounded-xl font-bold bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 dark:text-zinc-100 dark:placeholder:text-zinc-500"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-[9px] font-black uppercase tracking-tighter text-zinc-400">Neural Category</Label>
                                                <Select
                                                    value={newPersonality.category || 'general'}
                                                    onValueChange={(val) => setNewPersonality({ ...newPersonality, category: val })}
                                                >
                                                    <SelectTrigger className="h-11 rounded-xl bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 font-bold text-xs">
                                                        <SelectValue placeholder="Category" />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-2xl border-zinc-200 dark:border-zinc-800">
                                                        <SelectItem value="general" className="rounded-xl">General Chat</SelectItem>
                                                        <SelectItem value="calendar" className="rounded-xl">Calendar Assistant</SelectItem>
                                                        <SelectItem value="content-parser" className="rounded-xl">Content Parser</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-3 mt-4">
                                    <div className="space-y-1">
                                        <Label className="text-[9px] font-black uppercase tracking-tighter text-zinc-400">Neural Engine</Label>
                                        <Select value={newPersonality.model_id} onValueChange={(v) => {
                                            const selectedModel = models.find(m => m.id === v);
                                            setNewPersonality({
                                                ...newPersonality,
                                                model_id: v,
                                                model: selectedModel?.name || ''
                                            });
                                        }}>
                                            <SelectTrigger className="h-11 rounded-xl font-mono text-[10px] font-bold uppercase tracking-widest bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 dark:text-zinc-100">
                                                <SelectValue placeholder="Select Neural Engine" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {models.length > 0 ? (
                                                    models.map(m => (
                                                        <SelectItem key={m.id} value={m.id}>
                                                            <div className="flex flex-col">
                                                                <span className="font-bold">{m.name}</span>
                                                                <span className="text-[8px] opacity-60 uppercase tracking-tighter">{m.provider}</span>
                                                            </div>
                                                        </SelectItem>
                                                    ))
                                                ) : (
                                                    <SelectItem disabled value="none">No Models Configured</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-[9px] font-black uppercase tracking-tighter text-zinc-400">Custom API Key (Optional)</Label>
                                            <span className="text-[8px] font-bold text-zinc-400 uppercase italic opacity-60">Overrides Neural Engine Key</span>
                                        </div>
                                        <Input
                                            type="password"
                                            placeholder="••••••••••••••••"
                                            value={newPersonality.api_key}
                                            onChange={(e) => setNewPersonality({ ...newPersonality, api_key: e.target.value })}
                                            className="h-11 rounded-xl font-mono text-[10px] bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 dark:text-zinc-100"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Neural Tools</Label>
                                <div className="grid grid-cols-1 gap-2">
                                    {[
                                        { id: 'tool_web_search', label: 'Web Access', icon: Globe },
                                        { id: 'tool_code_interpreter', label: 'Code Engine', icon: Code },
                                        { id: 'tool_image_gen', label: 'Visual Studio', icon: ImageIcon },
                                        { id: 'tool_calendar_mgmt', label: 'Scheduler', icon: Calendar },
                                        { id: 'tool_chain_of_thought', label: 'Chain of Thought', icon: BrainCircuit }
                                    ].map(tool => (
                                        <div key={tool.id} className="flex items-center justify-between p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-800">
                                            <div className="flex items-center gap-3">
                                                <tool.icon className={cn("w-4 h-4", newPersonality[tool.id] ? "text-blue-500" : "text-zinc-400")} />
                                                <span className="text-xs font-bold text-zinc-800 dark:text-zinc-100">{tool.label}</span>
                                            </div>
                                            <Switch
                                                checked={newPersonality[tool.id]}
                                                onCheckedChange={(val) => setNewPersonality({ ...newPersonality, [tool.id]: val })}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {newPersonality.tool_chain_of_thought && !['deepseek-r1-distill-llama-70b', 'deepseek-r1-distill-qwen-32b', 'o1', 'o3', 'gemini-2.0-flash-thinking'].some(m => newPersonality.model?.includes(m)) && (
                                    <div className="mt-3 p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 flex gap-2">
                                        <AlertCircle className="w-4 h-4 text-orange-600 shrink-0" />
                                        <p className="text-[10px] text-orange-700 dark:text-orange-400 font-medium">
                                            Warning: The selected model ({newPersonality.model}) may not natively support internal chain-of-thought processing. Enabling this may result in simulated reasoning or empty thought blocks.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Instructor Directive (System Prompt)</Label>
                                <textarea
                                    value={newPersonality.system_instructions}
                                    onChange={(e) => setNewPersonality({ ...newPersonality, system_instructions: e.target.value })}
                                    className="w-full min-h-[150px] p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-800 text-sm font-medium dark:text-zinc-200 placeholder:text-zinc-400 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all resize-none"
                                    placeholder="Enter system prompt instructions..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Public Description</Label>
                                <Input
                                    placeholder="Short summary for students..."
                                    value={newPersonality.description}
                                    onChange={(e) => setNewPersonality({ ...newPersonality, description: e.target.value })}
                                    className="h-12 rounded-2xl bg-zinc-50 dark:bg-zinc-800 border-none font-medium dark:text-zinc-100"
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="p-6 md:p-8 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-100 dark:border-zinc-800 flex flex-row gap-4">
                        <Button variant="ghost" className="flex-1 rounded-2xl h-14 font-bold dark:text-zinc-400 dark:hover:bg-zinc-800" onClick={() => setIsDialogOpen(false)}>Abort</Button>
                        <Button className="flex-[2] rounded-2xl h-14 bg-zinc-900 dark:bg-white dark:text-zinc-900 font-black uppercase tracking-widest shadow-xl" onClick={handleSavePersonality}>
                            {editingPersonality ? 'Update Intelligence' : 'Deploy Instructor'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
                <DialogContent className="sm:max-w-[425px] rounded-[3rem] p-8 border-none shadow-2xl font-body" aria-describedby="config-dialog-description">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">AI Settings</DialogTitle>
                        <DialogDescription id="config-dialog-description" className="text-sm text-zinc-500">
                            Global neural engine configuration for all ScholarsAnchor instructors.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-6">

                        <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 block">Maintenance Tools</Label>
                            <Button
                                variant="outline"
                                className="w-full h-11 rounded-xl border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300 gap-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all font-bold text-xs"
                                onClick={syncLegacyUsers}
                                disabled={loading}
                            >
                                <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} /> Force User Auth Sync
                            </Button>
                            <p className="text-[9px] text-zinc-400 mt-2 px-1">Synchronizes any users created via legacy methods with the Supabase Auth system.</p>
                        </div>
                    </div>
                    <Button variant="ghost" className="w-full h-12 rounded-2xl font-bold dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800" onClick={() => setIsConfigOpen(false)}>Close Settings</Button>
                </DialogContent>
            </Dialog>

            <BulkImportModal
                open={bulkImportOpen}
                onOpenChange={setBulkImportOpen}
                entityName="benchrex_users"
                onImportComplete={() => fetchData()}
            />
        </div>
    );
}
